import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../../router/all_routes";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  Switch,
  message,
  Alert,
  Badge,
  Tooltip,
} from "antd";
import { CompanyStatus } from "../../../types/company.types";
import superAdminService from "../../../services/super-admin/super-admin.service";
import {
  BuildOutlined,
  SaveOutlined,
  CloseOutlined,
  PhoneOutlined,
  MailOutlined,
  BankOutlined,
  NumberOutlined,
  InfoCircleOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import "./styles.css";
import { handleApiError } from "../../../utils/errorHandler";
import { useTranslation } from "react-i18next";
import FooterSection from "../../shared/components/footerSection";
import ilIlceData from "../../../core/data/json/il-ilce.json";
const { Option } = Select;
const { TextArea } = Input;

const CreateCompany = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { t } = useTranslation();
  const [cities, setCities] = useState<
    { il_adi: string; plaka_kodu: string }[]
  >([]);
  const [districts, setDistricts] = useState<
    { ilce_adi: string; ilce_kodu: string }[]
  >([]);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(
    form.getFieldValue("city")
  );


  // İlleri yükle
  useEffect(() => {
    if (ilIlceData && ilIlceData.data) {
      const cityList = ilIlceData.data.map((city) => ({
        il_adi: city.il_adi,
        plaka_kodu: parseInt(city.plaka_kodu.trim()).toString(),
      }));
      setCities(cityList);
    }

    // Form değişikliklerini dinle
    const formCity = form.getFieldValue("city");
    if (formCity && formCity !== selectedCity) {
      setSelectedCity(formCity);
    }
  }, []);

  // Şehir değiştiğinde ilçeleri güncelle
  useEffect(() => {
    if (selectedCity) {
      const cityData = ilIlceData.data.find(
        (city) => city.il_adi === selectedCity
      );
      if (cityData && cityData.ilceler) {
        setDistricts(cityData.ilceler);

        // İlçe değerini koru, sadece şehir değiştiyse sıfırla
        const currentDistrict = form.getFieldValue("district");
        const isDistrictValid = cityData.ilceler.some(
          (d) => d.ilce_adi === currentDistrict
        );

        if (!isDistrictValid) {
          form.setFieldsValue({ district: undefined });
        }
      } else {
        setDistricts([]);
      }
    } else {
      setDistricts([]);
    }
  }, [selectedCity, form]);

  // Şehir değişikliğini yönet
  const handleCityChange = (value: string) => {
    setSelectedCity(value);
  };

  // Şirket oluşturma işlemi
  const handleSubmit = async (values: any) => {
    try {
      setLoading(true);
      setErrorMessage(null);

      // Süper admin için şirket oluşturma API çağrısı
      await superAdminService.createCompany(values);
      message.success("Şirket başarıyla oluşturuldu!");
      navigate(all_routes.superAdminCompanies);
    } catch (error: any) {
      // Backend'den dönen hata mesajını al
      let errorMsg = 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.';
      
      if (error.standartError) {
        errorMsg = error.standartError.description || error.standartError.message || errorMsg;
      } else if (error.response?.data?.error?.description) {
        errorMsg = error.response.data.error.description;
      } else if (error.response?.data?.error?.message) {
        errorMsg = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.message) {
        errorMsg = error.message;
      }

      // Hata mesajını state'e set et (Alert component'inde gösterilecek)
      setErrorMessage(errorMsg);
      
      // Ayrıca handleApiError ile de göster (mesaj popup olarak)
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  // Varsayılan değerleri ayarlama
  const initialValues = {
    name: "",
    address: "",
    phone: "",
    email: "",
    taxNumber: "",
    taxOffice: "",
    status: CompanyStatus.ACTIVE,
  };


  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-4">
            <div className="my-auto mb-2">
              <h2 className="mb-1">
                <BuildOutlined className="text-primary" />
                Yeni Şirket Oluştur
              </h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={all_routes.superAdminCompanies}>Şirketler</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Yeni Şirket
                  </li>
                </ol>
              </nav>
            </div>
            <div className="head-icons ms-2">
              <Button
                danger
                icon={<CloseOutlined />}
                onClick={() => navigate(all_routes.superAdminCompanies)}
                className="me-2"
              >
                İptal
              </Button>
            </div>
          </div>
          {/* /Breadcrumb */}

          {errorMessage && (
            <Alert
              message="Hata"
              description={errorMessage}
              type="error"
              showIcon
              closable
              onClose={() => setErrorMessage(null)}
              className="mb-4"
              style={{ margin: "0 auto 20px" }}
            />
          )}

          <Card className="company-form-card">
            <Form
              form={form}
              layout="vertical"
              initialValues={initialValues}
              onFinish={handleSubmit}
              className="company-form"
            >
              {/* Temel Bilgiler */}
              <div className="form-section">
                <div className="section-header">
                  <h3 className="section-title">
                    <BuildOutlined className="text-primary" />
                    Temel Bilgiler
                  </h3>
                  <p className="text-muted">
                    Şirket için temel bilgileri buradan girebilirsiniz. Şirket
                    adı zorunlu alandır.
                  </p>
                </div>

                <Form.Item
                  name="name"
                  label="Şirket Adı"
                  rules={[{ required: true, message: "Şirket adı gereklidir" }]}
                >
                  <Input
                    prefix={<ApartmentOutlined />}
                    placeholder="Şirket adını girin"
                    className="form-input"
                    size="large"
                  />
                </Form.Item>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="email"
                      label="E-posta"
                      rules={[
                        {
                          type: "email",
                          message: "Geçerli bir e-posta adresi girin",
                        },
                      ]}
                    >
                      <Input
                        prefix={<MailOutlined />}
                        placeholder="E-posta adresini girin"
                        className="form-input"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="phone" label="Telefon">
                      <Input
                        prefix={<PhoneOutlined />}
                        placeholder="Telefon numarasını girin"
                        className="form-input"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="status" label="Durum">
                  <Select
                    className="form-select"
                    size="large"
                    style={{ padding: "0px" }}
                  >
                    <Option value={CompanyStatus.ACTIVE}>
                      <Badge status="success" text="Aktif" />
                    </Option>
                    <Option value={CompanyStatus.INACTIVE}>
                      <Badge status="warning" text="Pasif" />
                    </Option>
                    <Option value={CompanyStatus.DELETED}>
                      <Badge status="error" text="Silindi" />
                    </Option>
                  </Select>
                </Form.Item>

                <Row gutter={24}>
                  <Col xs={24} md={8}>
                    <Form.Item name="city" label="İl/Şehir">
                      <Select
                        placeholder="İl/Şehir seçin"
                        className="form-select"
                        size="large"
                        showSearch
                        onChange={handleCityChange}
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {cities.map((city) => (
                          <Option key={city.plaka_kodu} value={city.il_adi}>
                            {city.il_adi}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="district" label="İlçe">
                      <Select
                        placeholder="İlçe seçin"
                        className="form-select"
                        size="large"
                        showSearch
                        disabled={!selectedCity}
                        filterOption={(input, option) =>
                          (option?.children as unknown as string)
                            .toLowerCase()
                            .indexOf(input.toLowerCase()) >= 0
                        }
                      >
                        {districts.map((district) => (
                          <Option
                            key={district.ilce_kodu}
                            value={district.ilce_adi}
                          >
                            {district.ilce_adi}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="zipCode" label="Posta Kodu">
                      <Input
                        placeholder="Posta kodunu girin"
                        className="form-input"
                        size="large"
                        maxLength={5}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item name="address" label="Adres">
                  <TextArea
                    placeholder="Şirket adresini girin"
                    rows={3}
                    className="form-textarea"
                  />
                </Form.Item>
              </div>

              {/* Vergi Bilgileri */}
              <div className="form-section">
                <div className="section-header">
                  <h3 className="section-title">
                    <BankOutlined className="text-primary" />
                    Vergi Bilgileri
                  </h3>
                  <p className="text-muted">
                    Şirketin vergi numarası ve vergi dairesi bilgilerini girin.
                  </p>
                </div>

                <Row gutter={24}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="taxNumber"
                      label={
                        <span>
                          Vergi No
                          <Tooltip title="Şirketin vergi kimlik numarasını girin">
                            <InfoCircleOutlined style={{ marginLeft: 8 }} />
                          </Tooltip>
                        </span>
                      }
                      rules={[
                        { required: true, message: "Vergi no gereklidir" },
                      ]}
                    >
                      <Input
                        prefix={<NumberOutlined />}
                        placeholder="Vergi numarasını girin"
                        className="form-input"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="taxOffice"
                      label="Vergi Dairesi"
                      rules={[
                        { required: true, message: "Vergi dairesi gereklidir" },
                      ]}
                    >
                      <Input
                        prefix={<BankOutlined />}
                        placeholder="Vergi dairesini girin"
                        className="form-input"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              {/* Form İşlemleri */}
              <div className="form-actions">
                <Button
                  danger
                  className="me-3"
                  icon={<CloseOutlined />}
                  onClick={() => navigate(all_routes.superAdminCompanies)}
                  size="large"
                >
                  İptal
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  className="submit-button"
                  size="large"
                >
                  Şirketi Oluştur
                </Button>
              </div>
            </Form>
          </Card>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default CreateCompany;
